"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { readPendingAddons, clearPendingAddons } from "./AddOnsTile";

// Packages — THREE tiles in a row under the time slots (0366 → 0382; Jeremy, 2026-09-30):
//   1. Cross promotion — "Add a Thermae sauna" / "Add a Bathe soak": the guest picks how many guests
//      the other-venue voucher covers; worth that session at the standard rate (a private soak at
//      Bathe, the communal price at Thermae), paid at that less the discount.
//   2. Come back for more — a VOUCHER for N more sessions like this one (+1 … max_sessions), worth
//      N × this booking's session price, less the discount; locked to this venue (0401).
//   3. Upsells — the venue's POS products (Sessions Setup → Packages → Upsells), bought as add-ons.
//
// Two stages:
//   <PackagesInfoTile>     — booking page, before a hold exists. CONTROLLED by BookingFlow (one
//                            instance); packages + upsells are stashed in sessionStorage for checkout.
//   <PackagesCheckoutTile> — checkout: adopts the stash, then every change POSTs ids + counts and the
//                            SERVER returns the authoritative total (set_booking_packages /
//                            set_booking_addons). Prices shown here are display only.

export type PackageItem = {
  id: string;
  kind: "cross_promo" | "class_pack";
  name: string;
  blurb: string | null;
  discount_pct: number;
  target_venue: string | null;
  max_guests: number;
  voucher_values: number[] | null;
  sessions: number | null;
  max_sessions?: number;
  image_url: string | null;
  eyebrow?: string | null;
  title?: string | null;
};

/** Per-venue editable copy (Sessions Setup → Packages, 0388); null → the defaults below. */
export type PackageCopy = { heading: string | null; upsell_eyebrow: string | null; upsell_title: string | null; upsell_blurb: string | null };

export type UpsellItem = { id: string; name: string; price: number; description: string | null; image_url: string | null; category?: string | null };

export type PackageLine = { name: string; qty: number; total: number };

/** package id → guests (cross_promo) or sessions (class_pack). */
export type PackageSelection = Record<string, number>;
/** product id → qty. */
export type UpsellSelection = Record<string, number>;

/** How "Come back for more" is priced: this booking's session price (null before a time is picked). */
export type PackBasis = { sessionPrice: number | null };

const nzd = (n: number) =>
  new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 2 }).format(n);
const r2 = (n: number) => Math.round(n * 100) / 100;
const VENUE_LABEL: Record<string, string> = { thermae: "Thermae", bathe: "Bathe" };
const VENUE_IMAGE: Record<string, string> = {
  thermae: "/packages/thermae-commons.jpg",
  bathe: "/packages/bathe-communal.jpg",
};
const MAX_UPSELL_QTY = 20;

/* ─────────────────────────────── pricing (display) ─────────────────────────────── */

/** The crossed-out "was" figure: the voucher's face value. */
export function fullValue(item: PackageItem, count: number, basis: PackBasis): number {
  if (item.kind === "cross_promo") return item.voucher_values?.[Math.max(1, count) - 1] ?? 0;
  return r2(Math.max(0, count) * (basis.sessionPrice ?? 0));
}

/** What the line adds to the booking total. Mirrors set_booking_packages (0401). */
export function linePrice(item: PackageItem, count: number, basis: PackBasis): number {
  if (count <= 0) return 0;
  return r2(fullValue(item, count, basis) * (1 - item.discount_pct / 100));
}

const packMin = (p: PackageItem) => Math.max(1, p.sessions ?? 1);
const packMax = (p: PackageItem) => Math.max(packMin(p), p.max_sessions ?? 3);
const sessionsLabel = (n: number) => `+${n} session${n === 1 ? "" : "s"}`;

/** Summary line name for a package. */
function packLineName(p: { kind: string; name: string; title?: string | null }, n: number): string {
  return p.kind === "cross_promo"
    ? `${p.name} (${n} guest${n === 1 ? "" : "s"})`
    : `${p.name || "Come back for more"} (${sessionsLabel(n)})`;
}

function packLines(items: PackageItem[], sel: PackageSelection, basis: PackBasis): PackageLine[] {
  return items
    .filter((p) => (sel[p.id] ?? 0) > 0 && (p.kind === "cross_promo" || (basis.sessionPrice ?? 0) > 0))
    .map((p) => ({ name: packLineName(p, sel[p.id]), qty: 1, total: linePrice(p, sel[p.id], basis) }));
}

function upsellLines(items: UpsellItem[], sel: UpsellSelection): PackageLine[] {
  return items
    .filter((u) => (sel[u.id] ?? 0) > 0)
    .map((u) => ({ name: u.name, qty: sel[u.id], total: r2(u.price * sel[u.id]) }));
}

/** Cap each count to what the package allows; drop anything no longer offered. */
function normalisePacks(items: PackageItem[], sel: PackageSelection): PackageSelection {
  const out: PackageSelection = {};
  for (const p of items) {
    const n = sel[p.id] ?? 0;
    if (n <= 0) continue;
    out[p.id] = p.kind === "cross_promo" ? Math.min(n, p.max_guests) : Math.min(Math.max(n, packMin(p)), packMax(p));
  }
  return out;
}
function normaliseUpsells(items: UpsellItem[], sel: UpsellSelection): UpsellSelection {
  const out: UpsellSelection = {};
  for (const u of items) if ((sel[u.id] ?? 0) > 0) out[u.id] = Math.min(MAX_UPSELL_QTY, sel[u.id]);
  return out;
}

/** Apply one change (one class pack at a time). */
function nextPackSelection(sel: PackageSelection, items: PackageItem[], id: string, n: number): PackageSelection {
  const out = { ...sel };
  const item = items.find((p) => p.id === id);
  if (item?.kind === "class_pack" && n > 0) {
    for (const p of items) if (p.kind === "class_pack" && p.id !== id) delete out[p.id];
  }
  if (n <= 0) delete out[id];
  else out[id] = n;
  return normalisePacks(items, out);
}

/* ───────────────────── pre-booking selection stash (sessionStorage) ───────────────────── */
const PENDING_KEY = "aluume_pending_packages";
// Which booking-page VISIT the stash belongs to (Codex r4). A new visit gets a new id, so a slow save
// from an earlier checkout can never clear picks made on a later visit — even identical ones.
const PENDING_GEN_KEY = "aluume_pending_gen";
export function startPendingGeneration() {
  try { sessionStorage.setItem(PENDING_GEN_KEY, `${Date.now()}-${Math.random().toString(36).slice(2)}`); } catch { /* private mode */ }
}
function readPendingGeneration(): string | null {
  try { return sessionStorage.getItem(PENDING_GEN_KEY); } catch { return null; }
}
export function readPendingPackages(): PackageSelection {
  try { const raw = sessionStorage.getItem(PENDING_KEY); return raw ? (JSON.parse(raw) as PackageSelection) : {}; }
  catch { return {}; }
}
export function writePendingPackages(next: PackageSelection) {
  try { sessionStorage.setItem(PENDING_KEY, JSON.stringify(next)); } catch { /* private mode */ }
}
function clearPendingPackages() {
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* nothing */ }
}
/** Same picks, ignoring key order and zero quantities. */
function samePicks(a: Record<string, number>, b: Record<string, number>): boolean {
  const norm = (o: Record<string, number>) => JSON.stringify(Object.entries(o).filter(([, n]) => n > 0).sort(([x], [y]) => x.localeCompare(y)));
  return norm(a) === norm(b);
}

type Catalogue = {
  packages: PackageItem[];
  upsells: UpsellItem[];
  copy: PackageCopy | null;
  pack_base_price: number | null;
  /** Checkout only: this hold's session price (the basis for Come back for more). */
  session_price?: number | null;
};

function useCatalogue(url: string | null): Catalogue | null {
  const [cat, setCat] = useState<Catalogue | null>(null);
  useEffect(() => {
    if (!url) return;
    let active = true;
    fetch(url, { cache: "no-store" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then((j) => {
        if (active) setCat({
          packages: j.packages ?? [], upsells: j.upsells ?? [], copy: j.copy ?? null, pack_base_price: j.pack_base_price ?? null,
          session_price: j.session_price ?? null,
        });
      })
      // A failed catalogue read renders NOTHING and never rewrites the selection (QA r2-7).
      .catch(() => { if (active) setCat(null); });
    return () => { active = false; };
  }, [url]);
  return cat;
}

/* ─────────────────────────────── visuals ─────────────────────────────── */

// Ticket-stack illustration for class packs — drawn, not a stock photo, in the brand palette.
function TicketsArt() {
  return (
    <svg viewBox="0 0 320 110" className="h-full w-full" preserveAspectRatio="xMidYMid slice" role="img" aria-label="Class pack tickets">
      <rect width="320" height="110" fill="#efe8dc" />
      {[0, 1, 2].map((i) => (
        <g key={i} transform={`translate(${62 + i * 26} ${16 - i * 5}) rotate(${-8 + i * 7} 90 40)`}>
          <path d="M0 8a8 8 0 0 1 8-8h164a8 8 0 0 1 8 8v20a12 12 0 0 0 0 24v20a8 8 0 0 1-8 8H8a8 8 0 0 1-8-8V52a12 12 0 0 0 0-24z"
            fill={i === 2 ? "#393331" : "#f7f2ea"} stroke="#393331" strokeOpacity="0.25" />
          <line x1="128" y1="10" x2="128" y2="70" stroke={i === 2 ? "#f7f2ea" : "#393331"} strokeOpacity="0.35" strokeDasharray="3 4" />
          <text x="18" y="46" fontFamily="Georgia, serif" fontSize="18" fill={i === 2 ? "#f7f2ea" : "#393331"}>
            {i === 2 ? "Aluume" : "Session"}
          </text>
        </g>
      ))}
    </svg>
  );
}

function UpsellArt() {
  return (
    <svg viewBox="0 0 320 110" className="h-full w-full" preserveAspectRatio="xMidYMid slice" role="img" aria-label="Add a little extra">
      <rect width="320" height="110" fill="#efe8dc" />
      <circle cx="160" cy="55" r="30" fill="#f7f2ea" stroke="#393331" strokeOpacity="0.25" />
      <path d="M147 55h26M160 42v26" stroke="#393331" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/** Up to two product photos side by side, edge to edge — one landscape picture, no divider. */
function SplitImage({ urls }: { urls: string[] }) {
  return (
    <div className="flex h-full w-full">
      {urls.slice(0, 2).map((u, i) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img key={i} src={u} alt="" loading="lazy" className="block h-full min-w-0 flex-1 object-cover" />
      ))}
    </div>
  );
}

// ONE card shape for all three tiles, with fixed-height bands so the headings, text, price boxes
// and buttons line up across the row (Jeremy, 2026-09-30).
function Tile({
  media, eyebrow, badge, title, blurb, on, price, action,
}: {
  media: React.ReactNode; eyebrow: string; badge?: string | null; title: string; blurb?: string | null;
  on: boolean; price: React.ReactNode; action: React.ReactNode;
}) {
  // Five bands on a SUBGRID of the row (TileRow): each band is as tall as the tallest tile's
  // content in that row and no taller, so headings, blurbs, prices and buttons line up across the
  // tiles without reserving fixed space a short title doesn't need (Jeremy, 2026-09-30).
  return (
    <div className={`row-span-5 grid snap-start grid-rows-subgrid gap-y-0 overflow-hidden rounded-2xl border bg-white transition-colors ${on ? "border-ink shadow-[0px_14px_32px_-22px_rgba(57,51,48,0.35)]" : "border-ink/[0.14]"}`}>
      <div className="relative h-[108px] w-full overflow-hidden bg-cream-warm">
        {media}
        {badge && (
          <span className="absolute right-3 top-3 rounded-pill bg-white/95 px-2.5 py-1 text-[11px] uppercase tracking-[0.4px] text-ink">{badge}</span>
        )}
      </div>
      <div className="flex flex-col gap-1 px-4 pt-4">
        <p className="eyebrow m-0 whitespace-nowrap text-ink-mute">{eyebrow}</p>
        <p className="m-0 line-clamp-2 text-[18px] leading-[1.2] text-ink" style={{ fontFamily: "var(--font-display)" }}>{title}</p>
      </div>
      <p className="m-0 line-clamp-3 px-4 pt-3 text-[12.5px] font-light leading-[1.3] text-ink-soft">{blurb ?? ""}</p>
      {price != null ? (
        <>
          <div className="px-4 pt-3">{price}</div>
          <div className="flex flex-col justify-end px-4 pb-4 pt-3">{action}</div>
        </>
      ) : (
        // No single price (Add a little extra): the product list takes the price + button bands.
        <div className="row-span-2 flex flex-col justify-end px-4 pb-4 pt-3">{action}</div>
      )}
    </div>
  );
}

/** The shaded price box: what you pay, with the full price struck through. What it covers (guests /
 *  sessions) is on the stepper below, so it isn't repeated here (Jeremy, 2026-09-30). */
function PriceBox({ pay, was, note }: { pay: number; was?: number; note?: string }) {
  return (
    <div className="flex min-h-[48px] flex-col items-center justify-center rounded-xl bg-cream-warm px-3.5 py-2">
      <div className="flex flex-wrap items-baseline justify-center gap-x-2">
        <span className="text-[15px] leading-tight text-ink" style={{ fontFamily: "var(--font-display)" }}>{nzd(pay)}{note ? "*" : ""}</span>
        {was != null && was > pay && <span className="text-[11px] font-light text-ink-soft line-through">{nzd(was)}</span>}
      </div>
    </div>
  );
}

/** Marks a price with the asterisk that points at the terms under the tiles (Jeremy, 2026-09-30). */
const PEAK_NOTE = "see terms";

/** The price box's place-holder when there's no price yet. */
function PriceNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-[48px] items-center justify-center rounded-xl bg-cream-warm px-3.5 text-center text-[12px] font-light leading-[1.3] text-ink-soft">
      {children}
    </div>
  );
}

function Stepper({ label, count, min, max, onCount, format }: { label: string; count: number; min: number; max: number; onCount: (n: number) => void; format?: (n: number) => string }) {
  return (
    <div className="flex h-[40px] items-center justify-between">
      <span className="text-[12px] font-light text-ink-soft">{label}</span>
      <div className="inline-flex items-center rounded-pill border border-ink/[0.2]">
        {/* Below the minimum = remove. */}
        <button type="button" onClick={() => onCount(count - 1 < min ? 0 : count - 1)} aria-label={count <= min ? "Remove" : `One fewer — ${label}`}
          className="relative flex size-8 items-center justify-center rounded-l-pill text-[16px] after:absolute after:-inset-1.5 after:content-[''] font-light text-ink-soft hover:text-accent">−</button>
        <span aria-live="polite" className="min-w-[24px] text-center text-[13px] tabular-nums text-ink">{format ? format(count) : count}</span>
        <button type="button" onClick={() => onCount(count + 1)} disabled={count >= max} aria-label={`One more — ${label}`}
          className="relative flex size-8 items-center justify-center rounded-r-pill text-[16px] after:absolute after:-inset-1.5 after:content-[''] font-light text-ink hover:text-accent disabled:opacity-30">+</button>
      </div>
    </div>
  );
}

function AddButton({ onClick, label = "Add" }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" onClick={onClick}
      className="h-[40px] w-full rounded-pill border border-ink/[0.2] text-[12px] uppercase tracking-[0.4px] text-ink transition-colors hover:border-accent hover:text-accent">
      {label}
    </button>
  );
}

/* ─────────────────────────────── the three tiles ─────────────────────────────── */

function CrossPromoTile({ item, count, party, onCount }: { item: PackageItem; count: number; party: number; onCount: (n: number) => void }) {
  const basis: PackBasis = { sessionPrice: null };
  const shown = count > 0 ? count : Math.min(item.max_guests, Math.max(1, party));
  const image = item.image_url ?? VENUE_IMAGE[item.target_venue ?? ""];
  return (
    <Tile
      // eslint-disable-next-line @next/next/no-img-element
      media={image ? <img src={image} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
      eyebrow={item.eyebrow || "Bundle and save"}
      badge={item.discount_pct > 0 ? `Save ${Math.round(item.discount_pct)}%` : null}
      title={item.title || item.name}
      blurb={item.blurb}
      on={count > 0}
      price={<PriceBox pay={linePrice(item, shown, basis)} was={fullValue(item, shown, basis)} note={PEAK_NOTE} />}
      action={count > 0
        ? <Stepper label="Guests" count={count} min={1} max={item.max_guests} onCount={onCount} />
        : <AddButton onClick={() => onCount(shown)} />}
    />
  );
}

function ClassPackTile({ pack, count, basis, onCount }: { pack: PackageItem; count: number; basis: PackBasis; onCount: (n: number) => void }) {
  const shown = count > 0 ? count : packMin(pack);
  const priced = (basis.sessionPrice ?? 0) > 0;
  return (
    <Tile
      media={pack.image_url
        // eslint-disable-next-line @next/next/no-img-element
        ? <img src={pack.image_url} alt="" loading="lazy" className="h-full w-full object-cover" />
        : <TicketsArt />}
      eyebrow={pack.eyebrow || "Come back for more"}
      badge={pack.discount_pct > 0 ? `Save ${Math.round(pack.discount_pct)}%` : null}
      title={pack.title || "A second session?"}
      blurb={pack.blurb || "A voucher for more sessions like this one, emailed with your confirmation."}
      on={count > 0}
      price={priced
        ? <PriceBox pay={linePrice(pack, shown, basis)} was={fullValue(pack, shown, basis)} note={PEAK_NOTE} />
        : <PriceNote>Choose your guests to see the price</PriceNote>}
      action={count > 0
        ? <Stepper label="More sessions" count={count} min={packMin(pack)} max={packMax(pack)} onCount={onCount} />
        : <AddButton onClick={() => onCount(shown)} />}
    />
  );
}

function UpsellTile({ items, sel, onQty, copy }: { items: UpsellItem[]; sel: UpsellSelection; onQty: (id: string, n: number) => void; copy: PackageCopy | null }) {
  if (!items.length) return null;
  const on = items.some((u) => (sel[u.id] ?? 0) > 0);
  const photos = items.map((u) => u.image_url).filter((u): u is string => !!u);
  const merch = items.every((u) => u.category === "merch");
  return (
    <Tile
      media={photos.length ? <SplitImage urls={photos} /> : <UpsellArt />}
      eyebrow={copy?.upsell_eyebrow || "Add on"}
      title={copy?.upsell_title || "Add a little extra"}
      blurb={copy?.upsell_blurb || (merch
        ? "Take home a little bit of Queenstown with one of our merchandise items."
        : "A little something extra to make the most of your visit.")}
      on={on}
      price={null}
      action={
        <ul className="flex flex-col divide-y divide-ink/10">
          {items.map((u) => {
            const q = sel[u.id] ?? 0;
            return (
              <li key={u.id} className="flex min-h-[46px] items-center justify-between gap-3 py-1.5">
                <div className="flex min-w-0 flex-col">
                  <span className="line-clamp-1 text-[13px] leading-[1.3] text-ink">{u.name}</span>
                  <span className="text-[12px] font-light text-ink-soft">{nzd(u.price)}</span>
                </div>
                {q > 0 ? (
                  <div className="inline-flex shrink-0 items-center rounded-pill border border-ink/[0.2]">
                    <button type="button" onClick={() => onQty(u.id, q - 1)} aria-label={`Remove one ${u.name}`}
                      className="relative flex size-8 items-center justify-center text-[15px] font-light text-ink-soft after:absolute after:-inset-1.5 after:content-[''] hover:text-accent">−</button>
                    <span className="min-w-[24px] text-center text-[12.5px] tabular-nums text-ink">{q}</span>
                    <button type="button" onClick={() => onQty(u.id, Math.min(MAX_UPSELL_QTY, q + 1))} aria-label={`Add another ${u.name}`}
                      className="relative flex size-8 items-center justify-center text-[15px] font-light text-ink after:absolute after:-inset-1.5 after:content-[''] hover:text-accent">+</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => onQty(u.id, 1)}
                    className="relative min-h-[32px] shrink-0 rounded-pill border border-ink/[0.2] px-3.5 py-1.5 text-[11.5px] after:absolute after:-inset-1.5 after:content-[''] uppercase tracking-[0.4px] text-ink hover:border-accent hover:text-accent">
                    Add
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      }
    />
  );
}

function TileRow({
  cat, sel, upSel, party, basis, onPack, onUpsell, narrow = false,
}: {
  cat: Catalogue; sel: PackageSelection; upSel: UpsellSelection; party: number; basis: PackBasis;
  onPack: (id: string, n: number) => void; onUpsell: (id: string, n: number) => void; narrow?: boolean;
}) {
  const cross = cat.packages.filter((p) => p.kind === "cross_promo");
  const pack = cat.packages.find((p) => p.kind === "class_pack") ?? null;
  if (!cross.length && !pack && !cat.upsells.length) return null;
  return (
    <div className={`-mx-6 grid snap-x auto-cols-[272px] grid-flow-col gap-x-4 gap-y-4 overflow-x-auto px-6 pb-2 scroll-pl-6 md:mx-0 md:auto-cols-auto md:grid-flow-row md:grid-cols-2 md:overflow-visible md:px-0 ${narrow ? "" : "xl:grid-cols-3"}`}>
      {cross.map((p) => (
        <CrossPromoTile key={p.id} item={p} count={sel[p.id] ?? 0} party={party} onCount={(n) => onPack(p.id, n)} />
      ))}
      {pack && <ClassPackTile pack={pack} count={sel[pack.id] ?? 0} basis={basis} onCount={(n) => onPack(pack.id, n)} />}
      <UpsellTile items={cat.upsells} sel={upSel} onQty={onUpsell} copy={cat.copy} />
    </div>
  );
}

/** The footnote for the price box's "Surcharges may apply*" (Jeremy, 2026-09-30). */
function PackagesDisclaimer({ cat }: { cat: Catalogue }) {
  if (!cat.packages.length) return null;
  return (
    <p className="m-0 text-[11px] font-light leading-[1.4] text-ink-soft">
      * Packages are priced off base session pricing. At peak times of the day, additional charges do
      apply. Package vouchers are emailed with your booking confirmation and are valid for the same
      period as our gift vouchers.
    </p>
  );
}




/* ───────────────────────── booking page (no hold yet) ───────────────────────── */
export function PackagesInfoTile({
  venue, partySize, sessionPrice, sel, onSel, upSel, onUpSel, onPackageLines, onUpsellLines, sectionRef,
}: {
  venue: "bathe" | "thermae";
  partySize: number;
  /** The selected time's session price for this party (null before a time is picked). */
  sessionPrice: number | null;
  sel: PackageSelection;
  onSel: (next: PackageSelection) => void;
  upSel: UpsellSelection;
  onUpSel: (next: UpsellSelection) => void;
  onPackageLines?: (lines: PackageLine[]) => void;
  onUpsellLines?: (lines: PackageLine[]) => void;
  /** Scroll target — BookingFlow centres this once a time is picked. */
  sectionRef?: React.Ref<HTMLElement>;
}) {
  const cat = useCatalogue(`/api/packages?venue=${venue}`);
  const basis: PackBasis = { sessionPrice };

  useEffect(() => {
    if (!cat) return;
    const p = normalisePacks(cat.packages, sel);
    if (JSON.stringify(p) !== JSON.stringify(sel)) onSel(p);
    const u = normaliseUpsells(cat.upsells, upSel);
    if (JSON.stringify(u) !== JSON.stringify(upSel)) onUpSel(u);
  }, [cat, partySize, sel, upSel, onSel, onUpSel]);

  useEffect(() => {
    if (!cat) return;
    onPackageLines?.(packLines(cat.packages, sel, { sessionPrice }));
    onUpsellLines?.(upsellLines(cat.upsells, upSel));
  }, [cat, sel, upSel, sessionPrice, onPackageLines, onUpsellLines]);

  if (!cat || (!cat.packages.length && !cat.upsells.length)) return <section ref={sectionRef} />;

  return (
    <section ref={sectionRef} className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="eyebrow m-0 text-ink-mute">Packages</p>
        <p className="m-0 text-[22px] font-light text-ink" style={{ fontFamily: "var(--font-display)" }}>{cat.copy?.heading || "Add to your visit"}</p>
      </div>
      <TileRow
        cat={cat}
        sel={sel}
        upSel={upSel}
        party={partySize}
        basis={basis}
        onPack={(id, n) => onSel(nextPackSelection(sel, cat.packages, id, n))}
        onUpsell={(id, n) => {
          const next = { ...upSel };
          if (n <= 0) delete next[id]; else next[id] = Math.min(MAX_UPSELL_QTY, n);
          onUpSel(next);
        }}
      />
      <PackagesDisclaimer cat={cat} />
    </section>
  );
}

/* ───────────────────────── checkout (hold exists) ───────────────────────── */
type ServerPkgLine = { package_id: string; kind: string; name: string; unit_price: number; qty: number; guests: number | null; sessions: number | null };
type ServerAddonLine = { product_id: string | null; name: string; unit_price: number; qty: number };

function selFromPkgLines(lines: ServerPkgLine[]): PackageSelection {
  const q: PackageSelection = {};
  for (const l of lines) q[l.package_id] = l.kind === "cross_promo" ? Number(l.guests ?? 1) : Number(l.sessions ?? 1);
  return q;
}
function pkgSummary(lines: ServerPkgLine[]) {
  return {
    packagesTotal: lines.reduce((s, l) => s + Math.round(Number(l.unit_price) * 100) * Number(l.qty), 0) / 100,
    lines: lines.map((l) => ({
      name: packLineName({ kind: l.kind, name: l.name }, Number(l.kind === "cross_promo" ? l.guests ?? 1 : l.sessions ?? 1)),
      qty: 1,
      total: r2(Number(l.unit_price) * Number(l.qty)),
    })),
  };
}
function selFromAddonLines(lines: ServerAddonLine[]): UpsellSelection {
  const q: UpsellSelection = {};
  for (const l of lines) if (l.product_id) q[l.product_id] = Number(l.qty);
  return q;
}
function addonSummary(lines: ServerAddonLine[]) {
  return {
    addonsTotal: lines.reduce((s, l) => s + Math.round(Number(l.unit_price) * 100) * Number(l.qty), 0) / 100,
    lines: lines.map((l) => ({ name: l.name, qty: Number(l.qty), total: r2(Number(l.unit_price) * Number(l.qty)) })),
  };
}

/** Debounced, version-guarded commit of one selection (QA r2-1 barrier). */
function useCommitter<T extends Record<string, number>>(
  post: (next: T) => Promise<void>,
  onPending: (p: boolean) => void,
) {
  const latest = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const postRef = useRef(post);
  useEffect(() => { postRef.current = post; }, [post]);
  const run = useCallback(async (next: T, version: number) => {
    try { await postRef.current(next); } finally {
      if (version === latest.current && timer.current === null) onPending(false);
    }
  }, [onPending]);
  const schedule = useCallback((next: T, immediate = false) => {
    const version = ++latest.current;
    onPending(true);
    if (timer.current) clearTimeout(timer.current);
    if (immediate) { timer.current = null; void run(next, version); return; }
    timer.current = setTimeout(() => { timer.current = null; void run(next, version); }, 350);
  }, [onPending, run]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  return schedule;
}

export function PackagesCheckoutTile({
  bookingId, onTotals, onAddonTotals, onPending,
}: {
  bookingId: string;
  /** Unused since the checkout picker went (2026-10-01); kept so callers needn't change. */
  partySize?: number;
  onTotals: (t: { packagesTotal: number; lines: PackageLine[] }) => void;
  onAddonTotals: (t: { addonsTotal: number; lines: PackageLine[] }) => void;
  /** True from a change until the server holds the LATEST packages AND upsells — checkout holds
   *  "Continue to Payment" while it is. */
  onPending?: (pending: boolean) => void;
}) {
  const [, setSel] = useState<PackageSelection>({});
  const [, setUpSel] = useState<UpsellSelection>({});
  // `hydrate` covers the first load + stash adoption: Continue stays held from mount until the
  // server holds what the guest picked on the booking page (Codex r3-11).
  // `error` keeps Continue held after a failed read/save until the guest retries (Codex r4-4): the
  // booking must never go to payment with extras the guest didn't see saved.
  const pendingRef = useRef({ hydrate: true, packages: false, upsells: false, error: false });
  // The booking-page visit whose stash this checkout adopted — only that visit's stash may be cleared.
  const stashGenRef = useRef<string | null>(null);
  const onPendingRef = useRef(onPending);
  useEffect(() => { onPendingRef.current = onPending; }, [onPending]);
  const report = useCallback(() => {
    const p = pendingRef.current;
    onPendingRef.current?.(p.hydrate || p.packages || p.upsells || p.error);
  }, []);
  const setPkgPending = useCallback((v: boolean) => { pendingRef.current.packages = v; report(); }, [report]);
  const setUpPending = useCallback((v: boolean) => { pendingRef.current.upsells = v; report(); }, [report]);
  // A refused/failed save is said out loud — never silently turned into "nothing selected".
  const [saveError, setSaveErrorState] = useState(false);
  const setSaveError = useCallback((v: boolean) => {
    pendingRef.current.error = v;
    setSaveErrorState(v);
    report();
  }, [report]);
  const [reloadKey, setReloadKey] = useState(0);
  // Something picked on the book screen the server didn't keep (it went unavailable in between) —
  // with no picker here, say so; the summary shows exactly what's being paid for.
  const [dropped, setDropped] = useState(false);
  const onTotalsRef = useRef(onTotals);
  useEffect(() => { onTotalsRef.current = onTotals; }, [onTotals]);
  const onAddonTotalsRef = useRef(onAddonTotals);
  useEffect(() => { onAddonTotalsRef.current = onAddonTotals; }, [onAddonTotals]);

  const postPackages = useCallback(async (next: PackageSelection) => {
    const res = await fetch(`/api/bookings/${bookingId}/packages`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: Object.entries(next).filter(([, n]) => n > 0).map(([package_id, n]) => ({ package_id, qty: n, guests: n, sessions: n })) }),
    }).catch(() => null);
    // Saved AND readable → the booking-page stash has done its job. Refused, unreachable, or a 2xx
    // whose body can't be read → keep the stash, pause payment, and show what the server really holds
    // (re-read), so nothing is ever charged without appearing in the summary (Codex 2026-10-01).
    let lines: ServerPkgLine[] | null = null;
    if (res && res.ok) lines = await res.json().then((j) => (Array.isArray(j?.items) ? j.items as ServerPkgLine[] : null)).catch(() => null);
    // Clear only if the stash is still what this save sent — a slow reply must never wipe picks the
    // guest has since made for a NEW booking on the booking page (Codex r3).
    if (lines) { if (readPendingGeneration() === stashGenRef.current && samePicks(readPendingPackages(), next)) clearPendingPackages(); }
    else {
      setSaveError(true);
      lines = ((await fetch(`/api/bookings/${bookingId}/packages`).then((r) => r.json()).catch(() => ({}))).items ?? []) as ServerPkgLine[];
    }
    const saved = selFromPkgLines(lines);
    if (lines && res?.ok && droppedAny(next, saved)) setDropped(true);
    setSel(saved);
    onTotalsRef.current(pkgSummary(lines));
  }, [bookingId, setSaveError]);

  const postUpsells = useCallback(async (next: UpsellSelection) => {
    const res = await fetch(`/api/bookings/${bookingId}/addons`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: Object.entries(next).filter(([, n]) => n > 0).map(([product_id, qty]) => ({ product_id, qty })) }),
    }).catch(() => null);
    let lines: ServerAddonLine[] | null = null;
    if (res && res.ok) lines = await res.json().then((j) => (Array.isArray(j?.items) ? j.items as ServerAddonLine[] : null)).catch(() => null);
    if (lines) { if (readPendingGeneration() === stashGenRef.current && samePicks(readPendingAddons(), next)) clearPendingAddons(); }
    else {
      setSaveError(true);
      lines = ((await fetch(`/api/bookings/${bookingId}/addons`).then((r) => r.json()).catch(() => ({}))).items ?? []) as ServerAddonLine[];
    }
    const saved = selFromAddonLines(lines);
    if (res?.ok && droppedAny(next, saved)) setDropped(true);
    setUpSel(saved);
    onAddonTotalsRef.current(addonSummary(lines));
  }, [bookingId, setSaveError]);

  const schedulePkg = useCommitter<PackageSelection>(postPackages, setPkgPending);
  const scheduleUp = useCommitter<UpsellSelection>(postUpsells, setUpPending);

  // Load what's saved on this hold; if nothing, adopt the booking-page stash once (each kind).
  useEffect(() => {
    let active = true;
    pendingRef.current.hydrate = true;
    report();
    const get = (url: string) => fetch(url).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    Promise.all([get(`/api/bookings/${bookingId}/packages`), get(`/api/bookings/${bookingId}/addons`)])
      .then(([pj, aj]) => {
        if (!active) return;
        // A failed read is NOT "nothing saved": keep the stash for a reload and say so.
        if (!pj || !aj) setSaveError(true);
        const pl = (pj?.items ?? []) as ServerPkgLine[];
        const al = (aj?.items ?? []) as ServerAddonLine[];
        // The stash is cleared only once its POST succeeds AND its reply reads (postPackages /
        // postUpsells), so a stash still here means its save was never confirmed — re-send it even
        // when the hold already has lines ("Try again" after an ambiguous failure, Codex r2). With
        // no picker on checkout (2026-10-01) the stash is the guest's only choice, so nothing here
        // can be overwritten; the re-send's reply runs the dropped-item check.
        // Pin the visit on the FIRST load; a later "Try again" only re-sends that visit's picks — a
        // newer booking-page visit's stash belongs to a different hold.
        const gen = readPendingGeneration();
        if (stashGenRef.current === null) stashGenRef.current = gen;
        const sameVisit = gen === stashGenRef.current;
        const stashPk = pj && sameVisit ? readPendingPackages() : {};
        const stashUp = aj && sameVisit ? readPendingAddons() : {};
        // Adoption POSTs flip their own pending on BEFORE hydrate is released, so Continue is
        // never enabled in between.
        if (Object.keys(stashPk).length) { setSel(stashPk); schedulePkg(stashPk, true); }
        else { setSel(selFromPkgLines(pl)); onTotalsRef.current(pkgSummary(pl)); }
        if (Object.keys(stashUp).length) { setUpSel(stashUp); scheduleUp(stashUp, true); }
        else { setUpSel(selFromAddonLines(al)); onAddonTotalsRef.current(addonSummary(al)); }
      })
      .finally(() => {
        if (!active) return;
        pendingRef.current.hydrate = false;
        report();
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, reloadKey]);

  // Packages and add-ons are chosen on the BOOK screens only (Jeremy, 2026-10-01) — checkout shows no
  // picker. This component still carries the booking-page choices onto the hold (the stash adoption
  // above) and holds payment while that save is in flight; checkout's summary lists what was chosen.
  // All that is visible here is a failed save, which pauses payment until the guest retries.
  if (!saveError && !dropped) return null;
  return (
    <div className="flex flex-col gap-1.5">
      {saveError && (
        <p role="alert" className="text-[12px] text-corten">
          We couldn&rsquo;t update your extras, so payment is paused.{" "}
          <button type="button" className="underline" onClick={() => { setSaveError(false); setReloadKey((k) => k + 1); }}>Try again</button>
        </p>
      )}
      {dropped && (
        <p role="status" className="text-[12px] text-corten">
          Something you picked is no longer available, so it hasn&rsquo;t been added &mdash; your summary shows what you&rsquo;re paying for.
        </p>
      )}
    </div>
  );
}

/** Did the server keep less than was asked for (an item missing, or fewer of it)? */
function droppedAny(asked: Record<string, number>, saved: Record<string, number>): boolean {
  return Object.entries(asked).some(([id, n]) => n > 0 && (saved[id] ?? 0) < n);
}
